import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  autonomyLanes,
  audienceOptions,
  budgetBands,
  caseOutputs,
  clientTypes,
  communicationOptions,
  emptyDraft,
  expertOptions,
  fundingSources,
  needCategories,
  priorityOptions,
  regions,
  workflowStages,
  agentTeams,
  type Audience,
  type ProcurementDraft,
} from "./data";
import {
  calculateReadiness,
  createCasePlan,
  getMissingItems,
  type CasePlan,
} from "./agentEngine";
import "./styles.css";

type View = "dashboard" | "request" | "case" | "workforce" | "governance";
type CaseTab = "overview" | "outputs" | "communication";

const DRAFT_KEY = "procugap.marketplace.draft.v1";
const wizardSteps = ["Potreba", "Klient", "Rozsah", "Spolupráca", "Kontrola"];

function loadDraft(): ProcurementDraft {
  try {
    const stored = window.localStorage.getItem(DRAFT_KEY);
    return stored ? { ...emptyDraft, ...JSON.parse(stored) } : { ...emptyDraft };
  } catch {
    return { ...emptyDraft };
  }
}

function formatDate(value: string) {
  if (!value) return "Nie je určený";
  return new Intl.DateTimeFormat("sk-SK", { dateStyle: "long" }).format(new Date(`${value}T12:00:00`));
}

function makeCaseId(audience: Audience) {
  const segment = audience === "municipality" ? "VO" : audience === "business" ? "B2B" : "YOU";
  return `PG-${new Date().getFullYear()}-${segment}-${String(Date.now()).slice(-5)}`;
}

export default function App() {
  const [view, setView] = useState<View>("dashboard");
  const [selectedAudience, setSelectedAudience] = useState<Audience>("municipality");
  const [draft, setDraft] = useState<ProcurementDraft>(loadDraft);
  const [step, setStep] = useState(0);
  const [caseId, setCaseId] = useState("");
  const [casePlan, setCasePlan] = useState<CasePlan | null>(null);
  const [caseTab, setCaseTab] = useState<CaseTab>("overview");
  const [message, setMessage] = useState("");
  const [activity, setActivity] = useState<Array<{ author: string; body: string; time: string }>>([]);

  useEffect(() => {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [draft]);

  const selectedCategory = needCategories.find((category) => category.id === draft.categoryId);
  const audience = audienceOptions.find((option) => option.id === draft.audience) ?? audienceOptions[0];
  const visibleCategories = needCategories.filter((category) => category.audiences.includes(selectedAudience));
  const readiness = calculateReadiness(draft);
  const missingItems = getMissingItems(draft);
  const workforceSize = agentTeams.reduce((total, team) => total + team.agents.length, 0);

  const canContinue = useMemo(() => {
    if (step === 0) return Boolean(draft.categoryId && draft.title.trim() && draft.description.trim().length >= 20);
    if (step === 1) {
      return draft.audience === "individual"
        ? Boolean(draft.clientName.trim() && draft.email.trim())
        : Boolean(draft.clientName.trim() && draft.contactName.trim() && draft.email.trim());
    }
    if (step === 2) return Boolean(draft.deliveryPlace.trim() && draft.budgetBand && draft.deadline);
    if (step === 4) return draft.confirmation;
    return true;
  }, [draft, step]);

  function updateField<K extends keyof ProcurementDraft>(field: K, value: ProcurementDraft[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function toggleList(field: "priorities" | "expertSupport" | "communicationChannels", value: string) {
    setDraft((current) => ({
      ...current,
      [field]: current[field].includes(value)
        ? current[field].filter((item) => item !== value)
        : [...current[field], value],
    }));
  }

  function startRequest(nextAudience: Audience, categoryId = "") {
    setSelectedAudience(nextAudience);
    setDraft((current) => ({
      ...current,
      audience: nextAudience,
      categoryId,
      clientType: current.audience === nextAudience ? current.clientType : "",
      fundingSource: current.audience === nextAudience ? current.fundingSource : "",
      confirmation: false,
    }));
    setStep(0);
    setView("request");
  }

  function resetRequest() {
    setDraft({ ...emptyDraft, audience: selectedAudience });
    setStep(0);
    setCaseId("");
    setCasePlan(null);
    setActivity([]);
    window.localStorage.removeItem(DRAFT_KEY);
  }

  function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const names = Array.from(event.target.files ?? []).map((file) => file.name);
    updateField("fileNames", names);
  }

  function submitCase() {
    if (!draft.confirmation) return;
    const id = makeCaseId(draft.audience);
    const plan = createCasePlan(draft, id);
    setCaseId(id);
    setCasePlan(plan);
    setCaseTab("overview");
    setActivity([
      { author: "OR-01 Orchestrátor", body: "Prípad bol vytvorený a rozdelený medzi osem AI tímov.", time: "teraz" },
      { author: "DP-71 Data Protection Guard", body: "Aktivoval som režim minimalizácie údajov a audit komunikácie.", time: "teraz" },
      { author: "IN-02 Intake analytik", body: `Pripravenosť vstupu je ${plan.readiness} %. Chýbajúce informácie som zaradil do otázok.`, time: "teraz" },
    ]);
    setView("case");
  }

  function sendMessage() {
    const trimmed = message.trim();
    if (!trimmed) return;
    setActivity((current) => [
      ...current,
      { author: draft.clientName || "Klient", body: trimmed, time: "teraz" },
      { author: "OR-01 Orchestrátor", body: "Správu som zaradil do prípadu a prideľujem ju správnemu tímu.", time: "teraz" },
    ]);
    setMessage("");
  }

  function navigate(nextView: View) {
    if (nextView === "case" && !casePlan) {
      setView("dashboard");
      return;
    }
    setView(nextView);
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button className="brand" onClick={() => navigate("dashboard")} aria-label="ProcuGap domov">
          <span>PG</span>
          <div><strong>ProcuGap</strong><small>by ACLASA</small></div>
        </button>

        <nav className="side-nav" aria-label="Hlavná navigácia">
          <button className={view === "dashboard" ? "active" : ""} onClick={() => navigate("dashboard")}>
            <span>01</span><strong>Prehľad</strong>
          </button>
          <button className={view === "request" ? "active" : ""} onClick={() => startRequest(selectedAudience)}>
            <span>02</span><strong>Nový dopyt</strong>
          </button>
          <button className={view === "case" ? "active" : ""} onClick={() => navigate("case")} disabled={!casePlan}>
            <span>03</span><strong>Pracovný prípad</strong>
          </button>
          <button className={view === "workforce" ? "active" : ""} onClick={() => navigate("workforce")}>
            <span>04</span><strong>AI Workforce</strong>
          </button>
          <button className={view === "governance" ? "active" : ""} onClick={() => navigate("governance")}>
            <span>05</span><strong>Dôvera a dohľad</strong>
          </button>
        </nav>

        <div className="automation-card">
          <div><span>98 %</span><small>cieľ automatizácie práce</small></div>
          <div className="automation-track"><i /></div>
          <p>Právne a finančné rozhodnutia zostávajú človeku.</p>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div>
            <span className="eyebrow">Bezpečný agentný procurement</span>
            <strong>{view === "dashboard" ? "Marketplace" : view === "request" ? "Intake" : view === "case" ? caseId : view === "workforce" ? "AI Workforce" : "Governance"}</strong>
          </div>
          <div className="topbar-actions">
            <span className="system-state"><i /> Systém pripravený</span>
            <button className="primary-button" onClick={() => startRequest(selectedAudience)}>Nový dopyt</button>
          </div>
        </header>

        <main className="workspace">
          {view === "dashboard" && (
            <>
              <section className="page-intro dashboard-intro">
                <div>
                  <p className="kicker">ProcuGap Marketplace</p>
                  <h1>Povedzte, čo potrebujete. AI tímy zariadia zvyšok.</h1>
                  <p>Jeden vstup pre verejné obstarávanie, firemné nákupy aj osobné dopyty. Vy rozhodujete, agenti analyzujú, komunikujú, koordinujú a kontrolujú dodanie.</p>
                </div>
                <div className="intro-metrics" aria-label="Kapacita agentného systému">
                  <div><strong>{workforceSize}</strong><span>AI zamestnancov</span></div>
                  <div><strong>{agentTeams.length}</strong><span>špecializovaných tímov</span></div>
                  <div><strong>3</strong><span>úrovne oprávnenia</span></div>
                </div>
              </section>

              <section className="audience-section" aria-labelledby="audience-title">
                <div className="section-title-row">
                  <div><span>Krok 1</span><h2 id="audience-title">Pre koho obstarávame?</h2></div>
                  <p>Každý segment má vlastné pravidlá, jazyk a rozhodovacie brány.</p>
                </div>
                <div className="audience-grid">
                  {audienceOptions.map((option) => (
                    <button
                      key={option.id}
                      className={`audience-card ${selectedAudience === option.id ? "selected" : ""}`}
                      onClick={() => setSelectedAudience(option.id)}
                    >
                      <span>{option.code}</span>
                      <small>{option.label}</small>
                      <h3>{option.title}</h3>
                      <p>{option.description}</p>
                      <strong>{option.promise}</strong>
                    </button>
                  ))}
                </div>
              </section>

              <section className="need-section" aria-labelledby="need-title">
                <div className="section-title-row">
                  <div><span>Krok 2</span><h2 id="need-title">Čo potrebujete zabezpečiť?</h2></div>
                  <button className="text-button" onClick={() => startRequest(selectedAudience)}>Neviem to zaradiť</button>
                </div>
                <div className="need-grid">
                  {visibleCategories.map((category) => (
                    <button key={category.id} className={`need-card tone-${category.tone}`} onClick={() => startRequest(selectedAudience, category.id)}>
                      <span>{category.code}</span>
                      <small>{category.kind}</small>
                      <h3>{category.title}</h3>
                      <p>{category.description}</p>
                      <div>{category.examples.map((example) => <em key={example}>{example}</em>)}</div>
                    </button>
                  ))}
                </div>
              </section>

              <section className="agent-overview">
                <div className="agent-overview-visual">
                  <img src="/procugap-command-center.png" alt="Operačné centrum koordinujúce nákup, expertov a dodanie" />
                </div>
                <div className="agent-overview-copy">
                  <p className="kicker">Agentná prevádzka</p>
                  <h2>Jedna požiadavka spustí celú digitálnu organizáciu.</h2>
                  <p>Orchestrátor rozdelí prípad medzi právny, trhový, technický, komunikačný, finančný, dodací a bezpečnostný tím. Každý výstup má zdroj, verziu, oprávnenie a stopu schválenia.</p>
                  <button className="secondary-button" onClick={() => navigate("workforce")}>Pozrieť AI tímy</button>
                </div>
              </section>
            </>
          )}

          {view === "request" && (
            <section className="request-page">
              <div className="request-heading">
                <div>
                  <button className="back-button" onClick={() => setView("dashboard")}>Späť na prehľad</button>
                  <p className="kicker">{audience.label} / nový dopyt</p>
                  <h1>{wizardSteps[step]}</h1>
                </div>
                <div className="progress-copy">
                  <strong>{step + 1} / {wizardSteps.length}</strong>
                  <span>{readiness} % pripravené</span>
                  <button className="text-button small-reset" onClick={resetRequest}>Vymazať dopyt</button>
                </div>
              </div>

              <div className="stepper" aria-label="Postup formulára">
                {wizardSteps.map((label, index) => (
                  <button key={label} className={index === step ? "active" : index < step ? "done" : ""} onClick={() => index < step && setStep(index)}>
                    <span>{index + 1}</span><strong>{label}</strong>
                  </button>
                ))}
              </div>

              <div className="request-layout">
                <div className="form-panel">
                  {step === 0 && (
                    <div className="form-step">
                      <div className="form-title"><span>01</span><div><h2>Čo má byť výsledkom?</h2><p>Stačí opísať potrebu vlastnými slovami. Agent ju neskôr premení na presné zadanie.</p></div></div>
                      <div className="compact-options">
                        {needCategories.filter((category) => category.audiences.includes(draft.audience)).map((category) => (
                          <button key={category.id} className={draft.categoryId === category.id ? "selected" : ""} onClick={() => updateField("categoryId", category.id)}>
                            <span>{category.code}</span><strong>{category.title}</strong>
                          </button>
                        ))}
                      </div>
                      <label className="field"><span>Pracovný názov dopytu</span><input value={draft.title} onChange={(event) => updateField("title", event.target.value)} placeholder="Napr. Rekonštrukcia školskej jedálne" /></label>
                      <label className="field"><span>Opíšte, čo potrebujete a prečo</span><textarea rows={7} value={draft.description} onChange={(event) => updateField("description", event.target.value)} placeholder="Súčasný stav, požadovaný výsledok, počet používateľov, obmedzenia a všetko, čo už viete..." /><small>{draft.description.length} znakov, minimum 20</small></label>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="form-step">
                      <div className="form-title"><span>02</span><div><h2>Kto zadáva dopyt?</h2><p>Údaje použijeme na nastavenie oprávnení, zodpovedností a správnej komunikačnej cesty.</p></div></div>
                      <div className="field-grid">
                        <label className="field"><span>Typ klienta</span><select value={draft.clientType} onChange={(event) => updateField("clientType", event.target.value)}><option value="">Vyberte typ</option>{clientTypes[draft.audience].map((item) => <option key={item}>{item}</option>)}</select></label>
                        <label className="field"><span>{draft.audience === "individual" ? "Meno a priezvisko" : "Názov organizácie"}</span><input value={draft.clientName} onChange={(event) => updateField("clientName", event.target.value)} /></label>
                        {draft.audience !== "individual" && <label className="field"><span>IČO</span><input value={draft.ico} onChange={(event) => updateField("ico", event.target.value)} inputMode="numeric" /></label>}
                        <label className="field"><span>Kraj</span><select value={draft.region} onChange={(event) => updateField("region", event.target.value)}><option value="">Vyberte kraj</option>{regions.map((item) => <option key={item}>{item}</option>)}</select></label>
                        {draft.audience !== "individual" && <label className="field"><span>Kontaktná osoba</span><input value={draft.contactName} onChange={(event) => updateField("contactName", event.target.value)} /></label>}
                        <label className="field"><span>E-mail</span><input type="email" value={draft.email} onChange={(event) => updateField("email", event.target.value)} placeholder="meno@organizacia.sk" /></label>
                        <label className="field"><span>Telefón</span><input type="tel" value={draft.phone} onChange={(event) => updateField("phone", event.target.value)} /></label>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="form-step">
                      <div className="form-title"><span>03</span><div><h2>Rozsah, rozpočet a termín</h2><p>Agent použije tieto údaje na výber trhovej stratégie, kapacít a realistického harmonogramu.</p></div></div>
                      <div className="field-grid">
                        <label className="field wide"><span>Miesto dodania alebo realizácie</span><input value={draft.deliveryPlace} onChange={(event) => updateField("deliveryPlace", event.target.value)} placeholder="Obec, adresa alebo územie" /></label>
                        <label className="field"><span>Rozpočtový rámec</span><select value={draft.budgetBand} onChange={(event) => updateField("budgetBand", event.target.value)}><option value="">Vyberte rámec</option>{budgetBands.map((item) => <option key={item}>{item}</option>)}</select></label>
                        <label className="field"><span>Odhad bez DPH, ak ho poznáte</span><input value={draft.estimatedValue} onChange={(event) => updateField("estimatedValue", event.target.value)} inputMode="decimal" placeholder="EUR" /></label>
                        <label className="field"><span>Požadovaný termín</span><input type="date" value={draft.deadline} onChange={(event) => updateField("deadline", event.target.value)} /></label>
                        <label className="field"><span>Financovanie</span><select value={draft.fundingSource} onChange={(event) => updateField("fundingSource", event.target.value)}><option value="">Vyberte zdroj</option>{fundingSources[draft.audience].map((item) => <option key={item}>{item}</option>)}</select></label>
                      </div>
                      <fieldset className="choice-field"><legend>Čo je pre vás najdôležitejšie?</legend><div className="check-grid">{priorityOptions.map((item) => <label key={item} className={draft.priorities.includes(item) ? "checked" : ""}><input type="checkbox" checked={draft.priorities.includes(item)} onChange={() => toggleList("priorities", item)} /><span>{item}</span></label>)}</div></fieldset>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="form-step">
                      <div className="form-title"><span>04</span><div><h2>Ako má AI tím pracovať?</h2><p>Vyberte kanály a expertov. Komunikácia AI bude označená, zaznamenaná v spise a obmedzená pridelenými oprávneniami.</p></div></div>
                      <div className="toggle-stack">
                        <label><input type="checkbox" checked={draft.needsFullProcess} onChange={(event) => updateField("needsFullProcess", event.target.checked)} /><span><strong>Kompletné zabezpečenie procesu</strong><small>Od prípravy po dodanie, kontrolu a uzatvorenie.</small></span></label>
                        <label><input type="checkbox" checked={draft.needsMarketResearch} onChange={(event) => updateField("needsMarketResearch", event.target.checked)} /><span><strong>Prieskum trhu a dodávateľov</strong><small>Shortlist, preverenie, ceny a porovnateľnosť.</small></span></label>
                        <label><input type="checkbox" checked={draft.hasSpecification} onChange={(event) => updateField("hasSpecification", event.target.checked)} /><span><strong>Máme technickú špecifikáciu</strong><small>Agent ju preverí a navrhne doplnenia.</small></span></label>
                      </div>
                      <fieldset className="choice-field"><legend>Komunikačné kanály</legend><div className="check-grid">{communicationOptions.map((item) => <label key={item} className={draft.communicationChannels.includes(item) ? "checked" : ""}><input type="checkbox" checked={draft.communicationChannels.includes(item)} onChange={() => toggleList("communicationChannels", item)} /><span>{item}</span></label>)}</div></fieldset>
                      <fieldset className="choice-field"><legend>Koho môže AI tím zapojiť?</legend><div className="check-grid">{expertOptions.map((item) => <label key={item} className={draft.expertSupport.includes(item) ? "checked" : ""}><input type="checkbox" checked={draft.expertSupport.includes(item)} onChange={() => toggleList("expertSupport", item)} /><span>{item}</span></label>)}</div></fieldset>
                      <label className="field"><span>Doplňujúce požiadavky</span><textarea rows={4} value={draft.requirements} onChange={(event) => updateField("requirements", event.target.value)} placeholder="Citlivé informácie, zakázané kontakty, interné pravidlá, preferovaný spôsob schvaľovania..." /></label>
                      <label className="upload-zone"><input type="file" multiple onChange={handleFiles} /><span>Pridať dokumenty alebo fotografie</span><small>{draft.fileNames.length ? draft.fileNames.join(", ") : "PDF, DOCX, XLSX, obrázky; v prototype sa ukladá iba názov súboru"}</small></label>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="form-step review-step">
                      <div className="form-title"><span>05</span><div><h2>Skontrolujte zadanie</h2><p>Po vytvorení prípadu agenti začnú pracovať. Externé právne a finančné úkony zostanú za rozhodovacou bránou.</p></div></div>
                      <div className="review-grid">
                        <article><span>Segment</span><strong>{audience.label}</strong><p>{audience.promise}</p></article>
                        <article><span>Potreba</span><strong>{selectedCategory?.title ?? "Nezaradená"}</strong><p>{draft.title || "Bez názvu"}</p></article>
                        <article><span>Klient</span><strong>{draft.clientName || "Nedoplnené"}</strong><p>{draft.email || "Bez e-mailu"}</p></article>
                        <article><span>Rámec</span><strong>{draft.budgetBand || "Neurčený"}</strong><p>Termín: {formatDate(draft.deadline)}</p></article>
                      </div>
                      <div className="readiness-panel">
                        <div><span>Pripravenosť vstupu</span><strong>{readiness} %</strong></div>
                        <div className="readiness-track"><i style={{ width: `${readiness}%` }} /></div>
                        {missingItems.length > 0 && <p>Agenti si ešte vypýtajú: {missingItems.join(", ")}.</p>}
                      </div>
                      <label className="confirmation"><input type="checkbox" checked={draft.confirmation} onChange={(event) => updateField("confirmation", event.target.checked)} /><span><strong>Potvrdzujem správnosť vstupov a agentné spracovanie</strong><small>Rozumiem, že AI pripravuje a vykonáva prácu v medziach oprávnení. Rozhodnutia s právnym alebo finančným účinkom musí autorizovať oprávnený človek.</small></span></label>
                    </div>
                  )}

                  <div className="form-actions">
                    <button className="secondary-button" onClick={() => step === 0 ? setView("dashboard") : setStep((current) => current - 1)}>Späť</button>
                    {step < 4
                      ? <button className="primary-button" disabled={!canContinue} onClick={() => setStep((current) => current + 1)}>Pokračovať</button>
                      : <button className="primary-button" disabled={!canContinue} onClick={submitCase}>Vytvoriť a spustiť prípad</button>}
                  </div>
                </div>

                <aside className="live-agent-panel">
                  <div className="panel-heading"><span>LIVE</span><div><strong>Agentný tím sa skladá</strong><small>{workforceSize} AI zamestnancov pripravených</small></div></div>
                  <div className="live-readiness"><span>Pripravenosť</span><strong>{readiness} %</strong><div><i style={{ width: `${readiness}%` }} /></div></div>
                  <div className="agent-preview-list">
                    {agentTeams.slice(0, 6).map((team, index) => {
                      const active = index <= Math.min(step + 1, 3);
                      return <article key={team.id} className={active ? "active" : ""}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{team.name}</strong><small>{active ? "Analyzuje vstup" : "Čaká v poradí"}</small></div></article>;
                    })}
                  </div>
                  <div className="privacy-note"><strong>Diskrétny režim</strong><p>Údaje sa v tomto prototype ukladajú iba lokálne v prehliadači. Produkčné nasadenie vyžaduje izolovaný tenant, šifrovanie, RBAC, DPA a retenčné politiky.</p></div>
                </aside>
              </div>
            </section>
          )}

          {view === "case" && casePlan && (
            <section className="case-page">
              <div className="case-header">
                <div><p className="kicker">Aktívny agentný prípad</p><h1>{draft.title}</h1><div className="case-meta"><span>{casePlan.id}</span><span>{casePlan.segment}</span><span>{casePlan.route}</span></div></div>
                <div className="case-actions"><span className="working-state"><i /> 3 tímy pracujú</span><button className="secondary-button" onClick={() => setView("request")}>Doplniť zadanie</button></div>
              </div>

              <div className="case-tabs">
                <button className={caseTab === "overview" ? "active" : ""} onClick={() => setCaseTab("overview")}>Prehľad</button>
                <button className={caseTab === "outputs" ? "active" : ""} onClick={() => setCaseTab("outputs")}>Výstupy</button>
                <button className={caseTab === "communication" ? "active" : ""} onClick={() => setCaseTab("communication")}>Komunikácia</button>
              </div>

              {caseTab === "overview" && (
                <>
                  <div className="case-metrics">
                    <article><span>Automatizácia</span><strong>98 %</strong><small>cieľ práce agentov</small></article>
                    <article><span>Pripravenosť</span><strong>{casePlan.readiness} %</strong><small>kvalita vstupu</small></article>
                    <article><span>AI Workforce</span><strong>{workforceSize}</strong><small>8 tímov</small></article>
                    <article><span>Rozhodovacie brány</span><strong>5</strong><small>človek autorizuje</small></article>
                  </div>
                  <div className="case-overview-grid">
                    <section className="next-actions"><div className="panel-title"><span>Teraz</span><h2>Najbližšie kroky</h2></div>{casePlan.nextActions.map((action, index) => <article key={action}><span>{index + 1}</span><p>{action}</p></article>)}</section>
                    <section className="risk-panel"><div className="panel-title"><span>Guardrails</span><h2>Riziká a kontroly</h2></div>{casePlan.riskSignals.length ? casePlan.riskSignals.map((risk) => <p key={risk}>{risk}</p>) : <p>Vstup neobsahuje mimoriadny rizikový signál. Trust tím pokračuje v monitoringu.</p>}</section>
                  </div>
                  <section className="workflow-panel"><div className="panel-title"><span>End-to-end</span><h2>Operačný tok prípadu</h2></div><div className="workflow-list">{workflowStages.map((stage, index) => <article key={stage.id} className={index === 0 ? "active" : ""}><span>{stage.label}</span><div><h3>{stage.title}</h3><p>{stage.description}</p><small>{stage.owner}</small></div></article>)}</div></section>
                  <section className="assignments-panel"><div className="panel-title"><span>AI teams</span><h2>Pridelená pracovná sila</h2></div><div className="assignment-grid">{casePlan.assignments.map((assignment) => <article key={assignment.teamId}><div><span className={`status-dot ${assignment.status === "Pracuje" ? "active" : ""}`} /><strong>{assignment.name}</strong></div><small>{assignment.status}</small><p>{assignment.task}</p></article>)}</div></section>
                </>
              )}

              {caseTab === "outputs" && (
                <section className="outputs-panel"><div className="panel-title"><span>Case file</span><h2>Výstupy a dôkazy</h2></div><div className="output-list">{caseOutputs.map((output, index) => <article key={output}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{output}</strong><small>{index < 2 ? "Agent pripravuje prvú verziu" : "Čaká na predchádzajúci krok"}</small></div><em>{index < 2 ? "PRACUJE" : "V PORADÍ"}</em></article>)}</div></section>
              )}

              {caseTab === "communication" && (
                <div className="communication-layout">
                  <section className="message-panel"><div className="panel-title"><span>Secure thread</span><h2>Komunikácia prípadu</h2></div><div className="activity-feed">{activity.map((item, index) => <article key={`${item.author}-${index}`}><span>{item.author.slice(0, 2)}</span><div><strong>{item.author}</strong><p>{item.body}</p><small>{item.time}</small></div></article>)}</div><div className="message-composer"><textarea rows={3} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Napíšte tímu alebo doplňte informáciu..." /><button className="primary-button" onClick={sendMessage}>Odoslať tímu</button></div></section>
                  <aside className="channel-panel"><div className="panel-title"><span>Channels</span><h2>Povolené kanály</h2></div>{draft.communicationChannels.map((channel) => <p key={channel}><strong>{channel}</strong><span>Aktívny a auditovaný</span></p>)}<div className="ai-disclosure"><strong>AI sa vždy identifikuje</strong><p>Žiadne vydávanie sa za človeka. Hlasové nahrávanie je predvolene vypnuté a právny záväzok sa neodosiela bez autorizácie.</p></div></aside>
                </div>
              )}
            </section>
          )}

          {view === "workforce" && (
            <section className="workforce-page">
              <div className="page-intro"><div><p className="kicker">ProcuGap AI Workforce</p><h1>24 digitálnych zamestnancov. Osem tímov. Jedna zodpovedná stopa.</h1><p>Agenti vykonávajú rutinnú aj odbornú prípravnú prácu, komunikujú s trhom a expertmi a neustále si navzájom kontrolujú výstupy.</p></div><div className="workforce-score"><strong>98 %</strong><span>automatizácia vykonanej práce</span><small>0 % autonómnych právnych záväzkov</small></div></div>
              <div className="autonomy-grid">{autonomyLanes.map((lane) => <article key={lane.level} className={`lane-${lane.level.toLowerCase()}`}><span>{lane.level}</span><h2>{lane.title}</h2><p>{lane.detail}</p></article>)}</div>
              <div className="team-grid">{agentTeams.map((team) => <article key={team.id} className="team-card"><header><span>{team.agents.length}</span><div><small>AI TEAM</small><h2>{team.name}</h2></div></header><p>{team.mission}</p><div>{team.agents.map((agent) => <section key={agent.code}><span>{agent.code}</span><div><strong>{agent.title}</strong><small>{agent.capability}</small></div><em>{agent.autonomy}</em></section>)}</div></article>)}</div>
            </section>
          )}

          {view === "governance" && (
            <section className="governance-page">
              <div className="page-intro"><div><p className="kicker">Trust architecture</p><h1>Autonómia bez anonymity. Rýchlosť bez straty kontroly.</h1><p>Každý agent má identitu, účel, rozsah oprávnenia, prístup iba k nevyhnutným údajom a nemennú stopu. Externá strana vždy vie, že komunikuje s AI.</p></div><div className="trust-seal"><span>AI</span><strong>Transparent by design</strong><small>účinné pravidlá od 2. augusta 2026</small></div></div>
              <div className="governance-grid">
                <article><span>AI Act</span><h2>Transparentnosť a gramotnosť</h2><p>AI sa identifikuje pri priamej komunikácii. Prevádzkovateľ školí supervízorov, eviduje účel, model, pokyny a incidenty.</p><a href="https://eur-lex.europa.eu/eli/reg/2024/1689/oj" target="_blank" rel="noreferrer">Nariadenie (EÚ) 2024/1689</a></article>
                <article><span>GDPR</span><h2>Privacy by design</h2><p>Minimalizácia, oddelené tenanty, právny základ, retenčné lehoty, práva dotknutých osôb a DPIA pred rizikovým nasadením.</p><a href="https://eur-lex.europa.eu/eli/reg/2016/679/oj" target="_blank" rel="noreferrer">Nariadenie (EÚ) 2016/679</a></article>
                <article><span>ZVO + eGov</span><h2>Človek autorizuje výkon moci</h2><p>AI pripravuje spis a podania, ale výber, vylúčenie, záväzok, KEP/eID a formálne odoslanie vykoná oprávnená osoba.</p><a href="https://www.slov-lex.sk/ezbierky/pravne-predpisy/SK/ZZ/2015/343/" target="_blank" rel="noreferrer">Zákon č. 343/2015 Z. z.</a></article>
                <article><span>Cybersecurity</span><h2>Zero trust prevádzka</h2><p>Šifrovanie, minimálne oprávnenia, oddelenie citlivých spisov, dôveryhodné logy, incident response a kontrola dodávateľov modelov.</p><a href="https://www.slov-lex.sk/ezbierky/pravne-predpisy/SK/ZZ/2018/69/" target="_blank" rel="noreferrer">Zákon č. 69/2018 Z. z.</a></article>
              </div>
              <section className="permission-matrix"><div className="panel-title"><span>Permission gates</span><h2>Čo AI smie a čo nesmie</h2></div>{autonomyLanes.map((lane) => <article key={lane.level}><span>{lane.level}</span><div><strong>{lane.title}</strong><p>{lane.detail}</p></div></article>)}</section>
              <section className="communication-protocol"><div><p className="kicker">Externá komunikácia</p><h2>Právnici, projektanti, konzultanti, dodávatelia aj úrady.</h2></div><div className="protocol-grid"><article><span>01</span><strong>Identita</strong><p>Agent uvedie, že je AI zástupca ProcuGap, koho zastupuje a aký má mandát.</p></article><article><span>02</span><strong>Diskrétnosť</strong><p>Posiela iba minimálny kontext. Citlivý spis zostáva v oddelenom dátovom priestore.</p></article><article><span>03</span><strong>Záznam</strong><p>Správa, hovor, zdroj a výsledok sa zapíšu do auditnej stopy; nahrávka iba po oznámení a právnom posúdení.</p></article><article><span>04</span><strong>Eskalácia</strong><p>Neistota, konflikt, právny účinok alebo prekročenie mandátu okamžite zastaví automatické konanie.</p></article></div></section>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
